import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Sidebar } from '../components/Sidebar';
import { HeaderStrip } from '../components/HeaderStrip';
import { BottomBar } from '../components/BottomBar';
import { LeagueSwitcherSheet } from '../components/LeagueSwitcherSheet';
import { createLeagueApi } from '../utils/api';
import { useUserLeagues } from '../contexts/UserLeaguesContext';
import { TeamColorsProvider, useTeamColors } from '../contexts/TeamColorsContext';
import { youStyle } from '../utils/teamColors';
import { useHomeSummary } from '../contexts/HomeSummaryContext';
import { sidebarStatus, isSynced } from '../components/home/homeCardModel';

const ATTRIBUTION = {
  YAHOO: { prefix: 'Fantasy data provided by ', name: 'Yahoo Fantasy', href: 'https://sports.yahoo.com/fantasy/' },
  ESPN: { prefix: 'Fantasy data from ', name: 'ESPN Fantasy', href: 'https://www.espn.com/fantasy/' },
};

// Yahoo's API terms require this line, with a link to an official Yahoo
// Fantasy page and no logo, on every page that shows their data. Text only.
function ProviderAttribution({ provider }) {
  const a = ATTRIBUTION[String(provider || '').toUpperCase()];
  if (!a) return null;
  return (
    <footer className="ledger-attribution">
      {a.prefix}<a href={a.href} target="_blank" rel="noopener noreferrer">{a.name}</a>
    </footer>
  );
}

export function SidebarLayout(props) {
  return (
    <TeamColorsProvider leagueId={props.activeLeagueId} teamIds={props.teamIds}>
      <SidebarShell {...props} />
    </TeamColorsProvider>
  );
}

function SidebarShell({ children, tabs, activeTab, onTabChange, leagueName, headMeta, isLoading, onRefresh, activeLeagueId, activeSeason, provider, isHome = false, viewer = false, footer = null, api = null }) {
  const { leagues: rawLeagues } = useUserLeagues();
  const leagues = rawLeagues ?? [];
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { youSlot } = useTeamColors();
  const { summary } = useHomeSummary();
  const statusByLeague = useMemo(
    () => new Map((summary?.leagues ?? []).map(l => [String(l.league_id), sidebarStatus(l)]).filter(([, s]) => s)),
    [summary],
  );

  const handleSelectLeague = useCallback(async (leagueId) => {
    if (leagueId === activeLeagueId) return;
    // A league you're already in, with synced data, opens straight away: POST
    // /join re-checks membership with the provider live, which made every switch
    // slow. A joined league that never synced still goes through join (it syncs).
    if ((summary?.leagues ?? []).some(l => String(l.league_id) === String(leagueId) && isSynced(l))) {
      navigate(`/league/${leagueId}/home`);
      return;
    }
    try {
      const token = await getToken();
      const api = createLeagueApi(leagueId, (url, opts) =>
        fetch(url, { ...opts, headers: { ...opts?.headers, 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } })
      );
      await api.joinLeague();
    } catch (e) {
      console.error('League join failed:', e);
    }
    navigate(`/league/${leagueId}/home`);
  }, [activeLeagueId, getToken, navigate, summary]);

  return (
    <div
      className="ledger-shell"
      data-shared-view={viewer ? '' : undefined}
      style={{ display: 'flex', minHeight: '100vh', background: 'var(--ledger-bg)', position: 'relative', ...youStyle(youSlot) }}
    >
      {!viewer && (
        <Sidebar
          leagues={leagues}
          activeLeagueId={activeLeagueId}
          onSelectLeague={handleSelectLeague}
          isHome={isHome}
          statusByLeague={statusByLeague}
        />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', zIndex: 1 }}>
        <HeaderStrip
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
          leagueName={leagueName}
          headMeta={headMeta}
          isLoading={isLoading}
          onRefresh={onRefresh}
          onOpenSheet={() => setSheetOpen(true)}
          leagueId={activeLeagueId}
          provider={provider}
          activeSeason={activeSeason}
          viewer={viewer}
          api={api}
        />
        {/* With a footer, main grows so the footer sits on the bottom edge of a short page. */}
        <main className="sidebar-main" style={footer ? { flex: '1 0 auto' } : undefined}>
          <div id="dashboard-panel" role="tabpanel" aria-labelledby={`dashboard-tab-${activeTab}`}>
            {children}
          </div>
        </main>
        <ProviderAttribution provider={provider} />
        {footer}
      </div>

      {tabs?.length > 0 && (
        <BottomBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      )}

      {!viewer && (
        <LeagueSwitcherSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          leagues={leagues}
          activeLeagueId={activeLeagueId}
          activeSeason={activeSeason}
          onSelectLeague={handleSelectLeague}
        />
      )}
    </div>
  );
}

export default SidebarLayout;
